/**
 * Advisory SSE Frame Parser
 *
 * A pure, dependency-free chunk-buffering parser for Server-Sent-Events-shaped
 * text. Deliberately mirrors advisoryStage.ts's "no React, no Redux, no SDK"
 * header convention: this module imports nothing from React, Redux, fetch, or
 * `TextDecoder` — decoding bytes to string is deliberately NOT this module's
 * job. `useSupervisorTurn.ts` owns the `TextDecoder` and feeds this parser
 * decoded strings.
 *
 * Never fatal: this layer performs no `JSON.parse` and never throws. Unknown
 * event names are emitted verbatim — skipping them is the caller's concern,
 * not this parser's. A block with no `data:` line emits no frame at all,
 * since there is nothing meaningful to hand the caller.
 *
 * Written fresh. Mechanics (buffer, `/\r?\n\r?\n/` block split,
 * `event:`/`data:`/comment-line handling) were reproduced from an earlier
 * reference implementation's SSE hook, read only via `git show` — that
 * branch was never checked out and no identifier, import, filename or
 * fixture from it appears here.
 *
 * Heartbeat fact (verified live, 2026-08-09): agent-svc's supervisor route
 * passes `ping=HEARTBEAT_INTERVAL_SECONDS` to `EventSourceResponse`, so `: `
 * comment blocks WILL arrive on a real stream and must produce no frame —
 * this is a live requirement, not a hypothetical edge case.
 */

/** One fully-delimited SSE frame: an event name and its joined data payload. */
export interface AdvisorySseFrame {
  event: string;
  data: string;
}

const BLOCK_DELIMITER = /\r?\n\r?\n/;
const LINE_DELIMITER = /\r?\n/;
const DEFAULT_EVENT_NAME = 'message';

/**
 * Creates a stateful parser closing over a private buffer. Each call appends
 * `chunk` to the buffer, then repeatedly extracts and parses every complete
 * block (a block is anything up to and including a blank-line delimiter,
 * LF- or CRLF-terminated). Any trailing incomplete block is left in the
 * buffer for a future call — this is what makes a frame split across two
 * `reader.read()` chunks resolve to exactly one frame, emitted only once its
 * terminator arrives.
 */
export function createAdvisorySseFrameParser(): (
  chunk: string,
  onFrame: (frame: AdvisorySseFrame) => void
) => void {
  let buffer = '';

  return (chunk: string, onFrame: (frame: AdvisorySseFrame) => void): void => {
    buffer += chunk;

    while (true) {
      const match = buffer.match(BLOCK_DELIMITER);
      if (!match || typeof match.index !== 'number') {
        break;
      }

      const blockEnd = match.index;
      const delimiterLength = match[0].length;
      const block = buffer.slice(0, blockEnd);
      buffer = buffer.slice(blockEnd + delimiterLength);

      let eventName = DEFAULT_EVENT_NAME;
      const dataLines: string[] = [];

      for (const rawLine of block.split(LINE_DELIMITER)) {
        if (!rawLine || rawLine.startsWith(':')) {
          // Empty line within the block, or an SSE comment (sse-starlette's
          // heartbeat sends `: ping - <timestamp>`) — neither carries a frame.
          continue;
        }
        if (rawLine.startsWith('event:')) {
          eventName = rawLine.slice('event:'.length).trim();
        } else if (rawLine.startsWith('data:')) {
          // Strip AT MOST ONE leading space per the SSE convention, so a
          // second leading space is preserved in the payload.
          const rest = rawLine.slice('data:'.length);
          dataLines.push(rest.startsWith(' ') ? rest.slice(1) : rest);
        }
        // Any other line shape is ignored rather than thrown on.
      }

      if (dataLines.length > 0) {
        onFrame({ event: eventName, data: dataLines.join('\n') });
      }
    }
  };
}
